import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Users, Globe, TrendingDown, Heart, Eye, Handshake, Award, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&w=1920";
const ABOUT_IMG = "https://images.pexels.com/photos/9875683/pexels-photo-9875683.jpeg?auto=compress&cs=tinysrgb&w=800";
const TEAM_IMG = "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?w=800&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const VALUES = [
  { icon: Heart, title: "Honesty", desc: "We're upfront about everything - no hidden fees, no surprises." },
  { icon: Eye, title: "Transparency", desc: "Clear, unbiased comparisons so you can make informed decisions." },
  { icon: TrendingDown, title: "Savings First", desc: "Our primary goal is finding you the best possible deal." },
  { icon: Handshake, title: "Simplicity", desc: "We make comparing and switching as easy as possible." },
  { icon: Award, title: "Customer Service", desc: "Dedicated Australian team available when you need us." },
  { icon: Globe, title: "Australia-Wide", desc: "From metro to regional, we service every corner of Australia." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero with background */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">About Us</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mt-2 mb-4">
              Helping Australians Find Better Deals
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Sales Corp Australia is a Sydney-based comparison broker helping households and businesses across Australia compare and switch to cheaper electricity providers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <img src={ABOUT_IMG} alt="Australian homes with solar" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2E2E33] mt-2 mb-6">
                Making Comparisons Simple, Savings Real
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                We believe every Australian deserves access to the best deals on essential services. Too often, people stay with expensive providers simply because comparing options seems too hard.
              </p>
              <p className="text-[#64748B] leading-relaxed mb-6">
                That's where we come in. Our team of experienced comparison specialists analyses the market daily to find you a plan that saves you real money on your electricity.
              </p>
              <div className="flex flex-wrap gap-6">
                {[
                  { num: "15,000+", label: "Customers Helped" },
                  { num: "$4.5M+", label: "Savings Delivered" },
                  { num: "$4.5M+", label: "Savings Delivered" },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-2xl font-extrabold text-[#0B203B]">{s.num}</p>
                    <p className="text-xs text-[#64748B]">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2E2E33] mt-2">What We Stand For</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl border border-[#E2E8F0] p-6 card-lift"
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF6600]/10 flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-[#FF6600]" strokeWidth={1.5} />
                </div>
                <h3 className="font-bold text-[#0B203B] text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Our Team</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2E2E33] mt-2 mb-6">
                Why Trust Sales Corp Australia?
              </h2>
              <ul className="space-y-4">
                {[
                  "Sydney-based team with nationwide coverage",
                  "Experienced comparison specialists in energy markets",
                  "Independent and unbiased - we work for you, not the providers",
                  "Ongoing support even after you switch",
                  "No cost, no obligation - ever",
                  "Thousands of satisfied Australian customers",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-[#64748B] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/compare" className="inline-block mt-8">
                <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3" data-testid="about-compare-btn">
                  Start Comparing <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <img src={TEAM_IMG} alt="Our support team" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA with background */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find a Better Deal?</h2>
          <p className="text-gray-300 mb-8">Get your free comparison today - it only takes a couple of minutes.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/compare">
              <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3" data-testid="about-cta-compare">
                Compare Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1300796797">
              <Button variant="outline" className="border-2 border-white/30 text-white font-bold rounded-full px-8 py-3 hover:bg-white hover:text-[#0B203B]" data-testid="about-cta-call">
                <Phone className="w-4 h-4 mr-2" /> 1300 796 797
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
